import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useUser } from '@/contexts/UserContext';
import type { User as UserType } from '@/utils/auth/type';
import { isAdminOrManagerRole } from '@/constants/roles';
import {
  User,
  Building2,
  Users,
  Shield,
  FormInput,
  Phone,
  PhoneOutgoing,
  ChevronRight,
  Settings,
  ChevronDown,
  Key,
  Database,
  History,
  RefreshCw,
  Layers,
  UserCheck,
  Sliders,
  Gauge,
  ListOrdered,
  AlarmClock,
  Workflow,
  BarChart3,
  IndianRupee
} from 'lucide-react';
import WhatsAppIcon from '@/components/icons/WhatsAppIcon';

interface NavItem {
  name: string;
  path: string;
  icon: React.ReactNode;
  description?: string;
  badge?: string;
  comingSoon?: boolean;
  superAdminOnly?: boolean;   // role_id 9401 only
}

interface NavSection {
  title: string;
  items: NavItem[];
  icon?: React.ReactNode;
}

const SettingsSidebar = () => {
  const location = useLocation();
  const { userData: apiResponse } = useUser(); // Use shared context
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set(['Security', 'User Management', 'System Configuration', 'Integrations', 'Round Robin Assignment']));
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [userData, setUserData] = useState<UserType | null>(null);

  // Transform API data whenever it changes
  useEffect(() => {
    if (apiResponse && apiResponse.data) {
      const apiData = apiResponse.data;
      console.log('🔍 API Response from context:', apiResponse);

      // Transform API data to match UserType interface
      const transformedUser: UserType = {
        name: apiData.userName || '',
        phone: apiData.phone || '',
        email: apiData.userEmail || '',
        client_uuid: '', // Not available in API response
        client_name: apiData.client?.client_name || '',
        user_uuid: apiData.uuid || '',
        role_uuid: apiData.role?.uuid || '',
        role_name: apiData.role?.role_name || '',
        is_super_admin: apiData.role?.role_name?.toLowerCase().includes('admin') || false,
        role_permissions: [{
          add_contacts: true,
          add_event: true,
          add_leads: true,
          assign_leads: true,
          dashboard: true,
              delete_contacts: true,
              delete_event: true,
              delete_leads: true,
              edit_contacts: true,
              edit_event: true,
              edit_leads: true,
              export_leads: true,
              reports: true,
              trash: true,
              view_all_leads: true,
              view_contacts: true,
              view_event: true,
              view_leads: true
            }]
      };

      console.log('🔍 Transformed User Data:', transformedUser);
      setUserData(transformedUser);
    }
  }, [apiResponse]);

  // Navigation sections with icons and descriptions - Segregated by type
  const navSections: NavSection[] = [
    {
      title: 'Personal',
      icon: <User className="h-4 w-4" />,
      items: [
        {
          name: 'Profile',
          path: 'profile',
          icon: <User className="h-4 w-4" />,
          description: 'Manage your personal information'
        },
        {
          name: 'Accounts',
          path: 'accounts',
          icon: <Key className="h-4 w-4" />,
          description: 'Security and login settings'
        }
      ]
    },
    {
      title: 'Security',
      icon: <Shield className="h-4 w-4" />,
      items: [
        {
          name: 'Login History',
          path: 'login-history',
          icon: <History className="h-4 w-4" />,
          description: 'View your login activity and sessions'
        },
        {
          name: 'Session Management',
          path: 'session-management',
          icon: <Shield className="h-4 w-4" />,
          description: 'Manage your active sessions'
        }
      ]
    },
    {
      title: 'User Management',
      icon: <Users className="h-4 w-4" />,
      items: [
        {
          name: 'Roles',
          path: 'roles',
          icon: <Shield className="h-4 w-4" />,
          description: 'Define permissions and access',
          badge: 'Admin'
        },
        {
          name: 'Teams',
          path: 'teams',
          icon: <Building2 className="h-4 w-4" />,
          description: 'Organize team structure',
          badge: 'Admin'
        },
        {
          name: 'Users',
          path: 'users',
          icon: <Users className="h-4 w-4" />,
          description: 'Manage team members',
          badge: 'Admin'
        }
      ]
    },
    {
      title: 'Round Robin Assignment',
      icon: <RefreshCw className="h-4 w-4" />,
      items: [
        {
          name: 'Categories & Products',
          path: 'rr-assignment/categories',
          icon: <Layers className="h-4 w-4" />,
          description: 'Product hierarchy for routing',
          badge: 'Admin'
        },
        {
          name: 'Agent Mapping',
          path: 'rr-assignment/mapping',
          icon: <UserCheck className="h-4 w-4" />,
          description: 'Assign agents per product',
          badge: 'Admin'
        },
        {
          name: 'Caps & Limits',
          path: 'rr-assignment/caps',
          icon: <Sliders className="h-4 w-4" />,
          description: 'Per-agent workload caps',
          badge: 'Admin'
        },
        {
          name: 'Attendance',
          path: 'rr-attendance',
          icon: <AlarmClock className="h-4 w-4" />,
          description: 'Daily agent attendance log',
          badge: 'Admin',
        },
        {
          name: 'Assignment Queue',
          path: 'rr-queue',
          icon: <ListOrdered className="h-4 w-4" />,
          description: 'Leads waiting to be assigned',
        },
        {
          name: 'Workload',
          path: 'rr-workload',
          icon: <Gauge className="h-4 w-4" />,
          description: 'Live cap usage per agent',
        },
        {
          name: 'History',
          path: 'rr-history',
          icon: <History className="h-4 w-4" />,
          description: 'Assignment audit trail',
        }
      ]
    },
    {
      title: 'Customer Journeys',
      icon: <Workflow className="h-4 w-4" />,
      items: [
        {
          name: 'Product Journeys',
          path: 'product-journeys',
          icon: <Workflow className="h-4 w-4" />,
          description: 'Multi-channel post-purchase flow per product',
          badge: 'New'
        },
        {
          name: 'WhatsApp',
          path: 'whatsapp-cloud',
          icon: <WhatsAppIcon className="h-4 w-4" />,
          description: 'Connection, webhook health & message templates',
          badge: 'New'
        },
        {
          name: 'WhatsApp Reporting',
          path: 'whatsapp-report',
          icon: <BarChart3 className="h-4 w-4" />,
          description: 'Performance, response times & per-agent stats',
          badge: 'New'
        },
        {
          name: 'WhatsApp Billing',
          path: 'whatsapp-billing',
          icon: <IndianRupee className="h-4 w-4" />,
          description: 'What Meta has charged — by category, template & agent',
          badge: 'New',
          superAdminOnly: true
        }
      ]
    },
    {
      title: 'System Configuration',
      icon: <Settings className="h-4 w-4" />,
      items: [
        {
          name: 'Client Information',
          path: 'client-info',
          icon: <Building2 className="h-4 w-4" />,
          description: 'Manage organization details',
          badge: 'Admin'
        },
        {
          name: 'Form Fields',
          path: 'form-fields',
          icon: <FormInput className="h-4 w-4" />,
          description: 'Customize lead forms',
          badge: 'Admin'
        }
      ]
    },
    {
      title: 'Integrations',
      icon: <Database className="h-4 w-4" />,
      items: [
        {
          name: 'Phone',
          path: 'phone-integration',
          icon: <Phone className="h-4 w-4" />,
          description: 'Phone system integration'
        },
        {
          name: 'Outbound Calling',
          path: 'outbound-calling',
          icon: <PhoneOutgoing className="h-4 w-4" />,
          description: 'Configure click-to-call providers',
          badge: 'New'
        }
      ]
    }
    // {
    //   title: 'Email Marketing',
    //   icon: <Mail className="h-4 w-4" />,
    //   items: [
    //     {
    //       name: 'Email Branding',
    //       path: 'drip-campaigns/branding',
    //       icon: <Palette className="h-4 w-4" />,
    //       description: 'Customize email appearance',
    //       badge: 'New'
    //     },
    //     {
    //       name: 'Email Templates',
    //       path: 'drip-campaigns/templates',
    //       icon: <FormInput className="h-4 w-4" />,
    //       description: 'Manage reusable email templates',
    //       badge: 'New'
    //     },
    //     {
    //       name: 'Drip Campaigns',
    //       path: 'drip-campaigns',
    //       icon: <Mail className="h-4 w-4" />,
    //       description: 'Automated email sequences',
    //       badge: 'New'
    //     }
    //   ]
    // }
  ];

  // 🔐 Check if user has admin permissions based on role UUID
  // Admin items and sections are shown to:
  // 1. Super Administrator
  // 2. System Administrator
  // 3. Sales Director, Sales Manager, Support Manager
  const hasAdminAccess = () => {
    if (!userData || !userData.role_uuid) return false;
    return isAdminOrManagerRole(userData.role_uuid);
  };

  const showAdminItems = hasAdminAccess();

  // Debug logging
  console.log('🔍 Settings Sidebar Debug:', {
    userData,
    role_uuid: userData?.role_uuid,
    role_name: userData?.role_name,
    showAdminItems,
    hasAdminAccess: hasAdminAccess()
  });

  // Filter sections based on permissions
  // For non-admin users, only show Personal and Security sections
  // For admin users (Super Administrator, System Administrator, Manager), show all sections
  const allowedSectionsForNonAdmin = ['Personal', 'Security'];

  const visibleSections = navSections
    .filter(section => {
      // If user is admin, show all sections
      if (showAdminItems) return true;

      // For non-admin users, only show Personal and Security
      return allowedSectionsForNonAdmin.includes(section.title);
    })
    .map(section => ({
      ...section,
      items: section.items.filter(item => {
        if (item.superAdminOnly && apiResponse?.data?.role?.role_id !== 9401) return false;
        if (!item.badge) return true; // No badge = show to everyone
        if (item.badge === 'New') return true; // New features = show to everyone
        if (item.badge === 'Admin') return showAdminItems; // Admin items = check permissions
        return false; // Unknown badge type = hide
      })
    }))
    .filter(section => section.items.length > 0); // Remove empty sections

  // Helper function to check if a path is active
  const isPathActive = (itemPath: string) => {
    if (location.pathname.endsWith('/settings')) {
      return itemPath === 'profile';
    }

    // Remove '/settings/' prefix from current path
    const currentPath = location.pathname.replace('/settings/', '');

    // Exact match only - no nested path matching to prevent parent paths from being active
    return currentPath === itemPath;
  };

  // Helper function to generate the full href path
  const getFullPath = (path: string) => {
    return `/settings/${path}`;
  };

  // Toggle section collapse with auto-collapse behavior
  const toggleSection = (title: string) => {
    const newCollapsed = new Set(collapsedSections);
    if (newCollapsed.has(title)) {
      // Expanding this section - collapse all others except Personal
      const allSections = navSections.map(section => section.title);
      const sectionsToCollapse = allSections.filter(sectionTitle =>
        sectionTitle !== title && sectionTitle !== 'Personal'
      );
      sectionsToCollapse.forEach(sectionTitle => newCollapsed.add(sectionTitle));
      newCollapsed.delete(title);
    } else {
      // Collapsing this section
      newCollapsed.add(title);
    }
    setCollapsedSections(newCollapsed);
  };

  return (
    <div className={`${isCollapsed ? 'w-16' : 'w-72'} h-full bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 flex flex-col`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className={`flex items-center space-x-3 ${isCollapsed ? 'hidden' : ''}`}>
            <div className="bg-brandColor p-2 rounded-lg">
              <Settings className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Settings</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Manage your workspace</p>
            </div>
          </div>
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <ChevronRight className={`h-4 w-4 text-gray-500 transition-transform ${!isCollapsed ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-3 space-y-4">
        {visibleSections.map((section) => {
          const isSectionCollapsed = collapsedSections.has(section.title);
          const hasSectionActiveItem = section.items.some(item => isPathActive(item.path));
          
          return (
            <div key={section.title} className="space-y-1">
              {/* Section Header */}
              {!isCollapsed && (
                <button
                  onClick={() => toggleSection(section.title)}
                  className={`w-full flex items-center justify-between px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors ${hasSectionActiveItem ? 'text-brandColor dark:text-green-400' : ''}`}
                >
                  <div className="flex items-center space-x-2">
                    {section.icon}
                    <span>{section.title}</span>
                  </div>
                  <ChevronDown className={`h-3 w-3 transition-transform ${isSectionCollapsed ? '-rotate-90' : ''}`} />
                </button>
              )}

              {/* Section Items */}
              <div className={`space-y-0.5 ${isSectionCollapsed && !isCollapsed ? 'hidden' : ''}`}>
                {section.items.map((item) => {
                  const isActive = isPathActive(item.path);
                  
                  if (item.comingSoon) {
                    return (
                      <div
                        key={item.path}
                        className="relative group cursor-not-allowed opacity-50"
                      >
                        <div className={`flex items-center px-3 py-2 rounded-lg text-gray-400 dark:text-gray-500 ${!isCollapsed ? 'space-x-3' : 'justify-center'}`}>
                          <div className="flex-shrink-0">{item.icon}</div>
                          {!isCollapsed && (
                            <div className="flex-1 flex items-center justify-between">
                              <div>
                                <span className="text-sm font-medium">{item.name}</span>
                                {item.description && (
                                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{item.description}</p>
                                )}
                              </div>
                              <span className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-500 px-2 py-0.5 rounded">Soon</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <Link
                      key={item.path}
                      to={getFullPath(item.path)}
                      className={`
                        relative group flex items-center px-3 py-2 rounded-lg transition-all duration-200
                        ${isActive
                          ? 'bg-green-50 dark:bg-green-900/20 text-brandColor dark:text-green-400 border-l-4 border-brandColor'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                        }
                        ${!isCollapsed ? 'space-x-3' : 'justify-center'}
                      `}
                    >
                      {/* Icon */}
                      <div className={`flex-shrink-0 ${isActive ? 'text-brandColor dark:text-green-400' : 'text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300'}`}>
                        {item.icon}
                      </div>

                      {/* Content */}
                      {!isCollapsed && (
                        <div className="flex-1 flex items-center justify-between">
                          <div>
                            <span className="text-sm font-medium">{item.name}</span>
                            {item.description && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{item.description}</p>
                            )}
                          </div>
                          {item.badge && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              item.badge === 'Admin'
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                : item.badge === 'New'
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            }`}>
                              {item.badge}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Tooltip for collapsed state */}
                      {isCollapsed && (
                        <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-10">
                          {item.name}
                        </div>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      {/* {!isCollapsed && (
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <Link
            to="/docs"
            className="flex items-center space-x-2 text-xs text-brandColor dark:text-green-400 hover:opacity-80 transition-colors"
          >
            <Sparkles className="h-3 w-3" />
            <span>Need help? Check our docs</span>
          </Link>
        </div>
      )} */}
    </div>
  );
};

export default SettingsSidebar;